import React, { useState, useMemo } from 'react';
import {
  Search,
  X,
  Loader2,
  Play,
  Clock,
  Disc3,
  RotateCcw,
} from 'lucide-react';
import { Track, Album } from '../types';
import { tg } from '../services/telegram';
import { userStore } from '../services/userStore';
import { audioEngine } from '../services/audioEngine';
import { TrackCard } from './TrackCard';
import { QueueRackIcon } from './icons/HardwareIcons';

export type QualityFilter = 'all' | 'hires' | 'cd' | 'fav';

interface SearchViewProps {
  query: string;
  onQueryChange: (q: string) => void;
  isLoading: boolean;
  tracks: Track[];
  albums?: Album[];
  currentTrack: Track | null;
  isPlaying: boolean;
  onSelectTrack: (track: Track) => void;
  recentSearches: string[];
  onSendFlacToBot?: (track: Track) => void;
}

export const SearchView: React.FC<SearchViewProps> = ({
  query,
  onQueryChange,
  isLoading,
  tracks,
  currentTrack,
  isPlaying,
  onSelectTrack,
  recentSearches,
  onSendFlacToBot,
}) => {
  const [qualityFilter, setQualityFilter] = useState<QualityFilter>('all');

  const filterButtons: Array<{ id: QualityFilter; label: string }> = [
    { id: 'all', label: 'Все' },
    { id: 'hires', label: 'Hi-Res 24-бит' },
    { id: 'cd', label: '16-бит CD' },
    { id: 'fav', label: 'В избранном' },
  ];

  // Фильтрация текущего списка треков
  const filteredTracks = useMemo(() => {
    return tracks.filter((track) => {
      if (qualityFilter === 'hires') {
        return (
          track.bitDepth === '24-bit' ||
          track.sampleRate.includes('96') ||
          track.sampleRate.includes('192') ||
          track.sampleRate.includes('88.2')
        );
      }
      if (qualityFilter === 'cd') {
        return track.bitDepth === '16-bit' || track.sampleRate.includes('44.1');
      }
      if (qualityFilter === 'fav') {
        return userStore.isFavorite(track.id);
      }
      return true;
    });
  }, [tracks, qualityFilter]);

  // Воспроизведение всей выборки
  const handlePlayAll = () => {
    if (filteredTracks.length === 0) return;
    tg.haptic('medium');
    audioEngine.setQueue(filteredTracks);
    audioEngine.play(filteredTracks[0]);
    if (query.trim().length >= 2) {
      userStore.addRecentSearch(query);
    }
  };

  const handleRecentClick = (text: string) => {
    tg.haptic('light');
    onQueryChange(text);
  };

  const handleRemoveRecent = (e: React.MouseEvent, text: string) => {
    e.stopPropagation();
    tg.haptic('light');
    userStore.removeRecentSearch(text);
  };

  const handleClearRecents = () => {
    tg.haptic('medium');
    userStore.clearRecentSearches();
  };

  const isSearchEmpty = !query.trim();

  return (
    <div className="search-view-container stagger-item stagger-4">
      {/* 1. ПОИСКОВАЯ ПАНЕЛЬ */}
      <div className="search-input-shell">
        <div className="search-input-box">
          <div className="search-icon-slot">
            {isLoading ? (
              <Loader2 size={15} className="spinning" color="var(--amber)" />
            ) : (
              <Search size={15} color="var(--champagne-dim)" />
            )}
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Трек, исполнитель или ссылка на альбом..."
            className="search-text-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            enterKeyHint="search"
            spellCheck="false"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
          {query && (
            <button
              type="button"
              className="search-clear-action-btn"
              onClick={() => {
                tg.haptic('light');
                onQueryChange('');
              }}
              aria-label="Очистить поиск"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* 2. ФИЛЬТРЫ КАЧЕСТВА */}
        <div className="search-quality-strip">
          {filterButtons.map((fb) => {
            const isActive = qualityFilter === fb.id;
            return (
              <button
                key={fb.id}
                type="button"
                className={`search-quality-chip ${isActive ? 'active' : ''}`}
                onClick={() => {
                  tg.haptic('light');
                  setQualityFilter(fb.id);
                }}
              >
                {fb.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. РЕЖИМ ПУСТОЙ СТРОКИ: ЧИСТО, БЕЗ ТОП АРТИСТОВ */}
      {isSearchEmpty ? (
        <>
          {/* Блок «Недавние запросы» пользователя (если есть в истории пользователя) */}
          {recentSearches.length > 0 && (
            <section className="search-section-block">
              <div className="search-section-header">
                <span className="search-section-title-wrap">
                  <Clock size={12} color="var(--amber)" />
                  <span>НЕДАВНИЕ ЗАПРОСЫ</span>
                </span>
                <button
                  type="button"
                  className="search-clear-recents-btn"
                  onClick={handleClearRecents}
                >
                  Очистить
                </button>
              </div>

              <div className="recent-searches-cloud">
                {recentSearches.map((item) => (
                  <div
                    key={item}
                    className="recent-search-pill"
                    onClick={() => handleRecentClick(item)}
                  >
                    <span className="recent-search-text">{item}</span>
                    <button
                      type="button"
                      className="recent-search-remove"
                      onClick={(e) => handleRemoveRecent(e, item)}
                      aria-label={`Удалить ${item}`}
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Чистое студийное приветствие без навязывания артистов */}
          <div className="search-clean-hero">
            <div className="search-clean-icon-wrap">
              <Disc3 size={28} color="var(--amber)" />
            </div>
            <div className="search-clean-title">ПОИСК В LOSSLESS-КАТАЛОГЕ</div>
            <div className="search-clean-desc">
              Введите название трека, имя исполнителя или отправьте ссылку из Spotify, Apple Music или Deezer для прямого студийного FLAC-потока.
            </div>
            <div className="search-specs-strip">
              <span className="search-spec-chip">24-BIT / 192 KHZ FLAC</span>
              <span className="search-spec-chip">БЕЗ СЖАТИЯ</span>
              <span className="search-spec-chip">BIT-PERFECT</span>
            </div>
          </div>
        </>
      ) : (
        /* 4. РЕЗУЛЬТАТЫ ПОИСКА (ТОЛЬКО КОГДА ВВЕДЁН ЗАПРОС) */
        <section className="tape-vault-card">
          <div className="search-vault-bar">
            <div className="vault-header-left">
              <QueueRackIcon size={12} color="var(--amber)" />
              <span className="search-vault-label">
                ПОИСК: «{query}»
              </span>
            </div>

            <div className="search-vault-actions">
              <span className="search-count-pill">
                НАЙДЕНО: {filteredTracks.length}
              </span>
              {filteredTracks.length > 0 && (
                <button
                  type="button"
                  className="play-all-vault-btn"
                  onClick={handlePlayAll}
                >
                  <Play size={10} fill="currentColor" />
                  <span>Слушать все</span>
                </button>
              )}
            </div>
          </div>

          {/* Состояние загрузки */}
          {isLoading && (
            <div className="search-loading-state" style={{ padding: '32px 16px', textAlign: 'center' }}>
              <Loader2 size={24} className="spinning" color="var(--amber)" style={{ margin: '0 auto 10px' }} />
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                Поиск в студийных каталогах Qobuz, Tidal и Apple Music...
              </div>
            </div>
          )}

          {/* Состояние «Ничего не найдено» или «Скрыто фильтром» */}
          {!isLoading && filteredTracks.length === 0 && (
            <div className="search-empty-state">
              <div className="search-empty-icon-wrap">
                <Disc3 size={32} color="var(--champagne-dim)" />
              </div>
              {tracks.length > 0 ? (
                <>
                  <div className="search-empty-title">Скрыто фильтром качества</div>
                  <div className="search-empty-desc">
                    По запросу «{query}» найдено треков: {tracks.length}, но они не соответствуют выбранному фильтру «{filterButtons.find(f => f.id === qualityFilter)?.label}».
                  </div>
                  <button
                    type="button"
                    className="search-empty-reset-btn"
                    onClick={() => {
                      tg.haptic('light');
                      setQualityFilter('all');
                    }}
                  >
                    <RotateCcw size={12} />
                    <span>Сбросить фильтр (показать все {tracks.length})</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="search-empty-title">Ничего не найдено</div>
                  <div className="search-empty-desc">
                    По запросу «{query}» не нашлось совпадений. Проверьте правильность написания или попробуйте изменить формулировку.
                  </div>
                  <button
                    type="button"
                    className="search-empty-reset-btn"
                    onClick={() => {
                      tg.haptic('light');
                      onQueryChange('');
                      setQualityFilter('all');
                    }}
                  >
                    <RotateCcw size={12} />
                    <span>Сбросить поиск</span>
                  </button>
                </>
              )}
            </div>
          )}

          {/* Список найденных треков */}
          {!isLoading && filteredTracks.length > 0 && (
            <div className="cassette-spine-list">
              {filteredTracks.map((t, idx) => (
                <TrackCard
                  key={`search-${t.id}-${idx}`}
                  track={t}
                  index={idx}
                  isActive={currentTrack ? t.id === currentTrack.id : false}
                  isPlaying={isPlaying}
                  onSelect={() => {
                    onSelectTrack(t);
                    if (query.trim().length >= 2) {
                      userStore.addRecentSearch(query);
                    }
                  }}
                  onSendFlacToBot={onSendFlacToBot}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};
